import { onMounted, ref, unref, watch } from 'vue';
import RealEstateFinanceAPI from 'dashboard/api/realEstateFinance';

export function useRealEstateFinanceInstallment({
  installmentId,
  notify,
  t = key => key,
}) {
  const selectedInstallment = ref(null);
  const statusCatalog = ref([]);
  const loadingDetail = ref(false);
  const loadingCatalog = ref(false);
  const actionLoading = ref('');
  const error = ref('');
  let requestVersion = 0;

  const notifyError = message => {
    error.value = message;
    if (notify) notify(message, 'error');
  };

  const load = async () => {
    const id = unref(installmentId);
    requestVersion += 1;
    const currentRequest = requestVersion;
    if (!id) {
      selectedInstallment.value = null;
      return null;
    }

    loadingDetail.value = true;
    error.value = '';
    try {
      const response = await RealEstateFinanceAPI.show(id);
      if (currentRequest !== requestVersion) return null;
      selectedInstallment.value = response.data;
      return selectedInstallment.value;
    } catch (requestError) {
      if (currentRequest !== requestVersion) return null;
      selectedInstallment.value = null;
      notifyError(
        requestError.response?.data?.message ||
          t('REAL_ESTATE_FINANCE.ERRORS.DETAIL')
      );
      return null;
    } finally {
      if (currentRequest === requestVersion) loadingDetail.value = false;
    }
  };

  const loadCatalog = async () => {
    loadingCatalog.value = true;
    try {
      const response = await RealEstateFinanceAPI.statusCatalog();
      statusCatalog.value = response.data?.data || [];
    } catch {
      notifyError(t('REAL_ESTATE_FINANCE.ERRORS.CATALOG'));
    } finally {
      loadingCatalog.value = false;
    }
  };

  const performAction = async (name, request, successMessage) => {
    actionLoading.value = name;
    try {
      const response = await request();
      await load();
      if (notify && successMessage) {
        notify(successMessage, null, { variant: 'success' });
      }
      return response.data;
    } catch (actionError) {
      notifyError(
        actionError.response?.data?.message ||
          t('REAL_ESTATE_FINANCE.ERRORS.OPERATION')
      );
      throw actionError;
    } finally {
      actionLoading.value = '';
    }
  };

  const currentInstallmentId = () =>
    selectedInstallment.value?.parcela?.parcela_id || unref(installmentId);

  const transitionInstallment = payload =>
    performAction(
      'transition',
      () => RealEstateFinanceAPI.transition(currentInstallmentId(), payload),
      t('REAL_ESTATE_FINANCE.ALERTS.STATUS_SAVED')
    );

  const updatePaymentResponsibilities = responsibilities =>
    performAction(
      'payers',
      () =>
        RealEstateFinanceAPI.updatePaymentResponsibilities(
          currentInstallmentId(),
          responsibilities
        ),
      t('REAL_ESTATE_FINANCE.ALERTS.PAYERS_SAVED')
    );

  const confirmPayment = (responsibilityId, payload) =>
    performAction(
      'payment',
      () =>
        RealEstateFinanceAPI.confirmPayment(
          currentInstallmentId(),
          responsibilityId,
          payload
        ),
      t('REAL_ESTATE_FINANCE.ALERTS.PAYMENT_SAVED')
    );

  const uploadBoleto = (responsibilityId, file, onUploadProgress) =>
    performAction(
      'boleto-upload',
      () =>
        RealEstateFinanceAPI.uploadBoleto(
          currentInstallmentId(),
          responsibilityId,
          file,
          onUploadProgress
        ),
      t('REAL_ESTATE_FINANCE.ALERTS.BOLETO_UPLOADED')
    );

  const updateBoleto = (responsibilityId, payload) =>
    performAction(
      'boleto-update',
      () =>
        RealEstateFinanceAPI.updateBoleto(
          currentInstallmentId(),
          responsibilityId,
          payload
        ),
      t('REAL_ESTATE_FINANCE.ALERTS.BOLETO_SAVED')
    );

  const removeBoleto = responsibilityId =>
    performAction(
      'boleto-delete',
      () =>
        RealEstateFinanceAPI.removeBoleto(
          currentInstallmentId(),
          responsibilityId
        ),
      t('REAL_ESTATE_FINANCE.ALERTS.BOLETO_DELETED')
    );

  const downloadBoleto = async responsibilityId => {
    actionLoading.value = 'boleto-download';
    try {
      const response = await RealEstateFinanceAPI.downloadBoleto(
        currentInstallmentId(),
        responsibilityId
      );
      const url = response.data?.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
      return url;
    } catch (actionError) {
      notifyError(
        actionError.response?.data?.message ||
          t('REAL_ESTATE_FINANCE.ERRORS.OPERATION')
      );
      throw actionError;
    } finally {
      actionLoading.value = '';
    }
  };

  const releaseSplit = beneficiary => {
    const type = beneficiary?.tipo || 'usuario';
    const id =
      type === 'empresa'
        ? beneficiary?.empresa?.id || beneficiary?.empresa_id
        : beneficiary?.usuario?.id || beneficiary?.usuario_id || beneficiary;

    return performAction(
      'split-release',
      () => RealEstateFinanceAPI.releaseSplit(currentInstallmentId(), id, type),
      t('REAL_ESTATE_FINANCE.ALERTS.SPLIT_RELEASED')
    );
  };

  watch(() => unref(installmentId), load, { immediate: true });
  onMounted(loadCatalog);

  return {
    actionLoading,
    confirmPayment,
    downloadBoleto,
    error,
    load,
    loadingCatalog,
    loadingDetail,
    releaseSplit,
    removeBoleto,
    selectedInstallment,
    statusCatalog,
    transitionInstallment,
    updateBoleto,
    updatePaymentResponsibilities,
    uploadBoleto,
  };
}
